/**
 * /whatsapp/instancias/:instanciaId — Detalhe + kill switch + QR modal.
 *
 * Polling: 15s padrao; 3s acelerado quando status === "AGUARDANDO_QR"
 * (QR expira em 60s, precisamos detectar conexao rapido).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiArrowLeft, FiPause, FiPlay, FiPower, FiRefreshCw } from "react-icons/fi";

import EntityHeader from "../../../components/EntityHeader";
import KpiStrip from "../../../components/KpiStrip";
import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import useWhatsappPolling from "../../../hooks/useWhatsappPolling";
import {
  desconectarInstancia,
  iniciarSessaoInstancia,
  obterInstancia,
  pausarInstancia,
  retomarInstancia,
  STATUS_INSTANCIA_LABEL,
  STATUS_INSTANCIA_TONE,
} from "../../../services/whatsapp";
import { PageContainer } from "../../../styles/shared";
import { fmtData } from "../../Orcamentos/helpers";
import {
  Block,
  Card,
  DetailGrid,
  EmptyMsg,
  ErrorMsg,
  Field,
  FieldLabel,
  FieldValue,
  FieldRow,
  Form,
  FormFooter,
  InlineHint,
  Pill,
  SecondaryButton,
  SectionTitle,
  Textarea,
} from "../../Orcamentos/styles";
import { KillSwitchBox, QrFrame } from "../styles";

export default function InstanciaDetalhe() {
  const { instanciaId } = useParams();
  const navigate = useNavigate();
  const { request, hasPermission } = useAuth();
  const podeGerenciar = hasPermission("whatsapp:manage");

  const [instancia, setInstancia] = useState(null);
  const [error, setError] = useState(null);

  const [qrOpen, setQrOpen] = useState(false);
  const [pauseOpen, setPauseOpen] = useState(false);
  const [pauseMotivo, setPauseMotivo] = useState("");

  const carregar = useCallback(async () => {
    setError(null);
    try {
      const data = await obterInstancia(request, instanciaId);
      setInstancia(data);
      // Auto-abrir modal QR se status mudou para AGUARDANDO_QR
      if (data.status === "AGUARDANDO_QR" && data.qr_code_atual && !qrOpen) {
        setQrOpen(true);
      }
      // Fechar modal QR se ja conectou
      if (data.status === "CONECTADA" && qrOpen) {
        setQrOpen(false);
      }
    } catch (err) {
      setError(err?.message || "Erro ao carregar instancia.");
    }
  }, [request, instanciaId, qrOpen]);

  // Polling acelerado (3s) quando aguardando QR; senao 15s
  const intervalo = instancia?.status === "AGUARDANDO_QR" ? 3000 : 15000;
  useWhatsappPolling(carregar, intervalo, [carregar, intervalo]);

  // Refresh inicial
  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instanciaId]);

  const handleIniciar = async () => {
    await iniciarSessaoInstancia(request, instanciaId);
    await carregar();
    // O QR fica visivel apos webhook do Node; polling 3s vai pegar.
  };

  const handleDesconectar = async () => {
    if (!window.confirm("Desconectar a sessao do WhatsApp Web?")) return;
    await desconectarInstancia(request, instanciaId);
    await carregar();
  };

  const handlePausar = async () => {
    if (!pauseMotivo.trim() || pauseMotivo.trim().length < 5) {
      throw new Error("Motivo é obrigatório (min 5 caracteres).");
    }
    await pausarInstancia(request, instanciaId, pauseMotivo.trim());
    setPauseOpen(false);
    setPauseMotivo("");
    await carregar();
  };

  const handleRetomar = async () => {
    await retomarInstancia(request, instanciaId);
    await carregar();
  };

  const kpis = useMemo(() => {
    if (!instancia) return [];
    return [
      {
        label: "Status",
        value: STATUS_INSTANCIA_LABEL[instancia.status] || instancia.status,
        tone: STATUS_INSTANCIA_TONE[instancia.status] || "neutral",
      },
      {
        label: "Número",
        value: instancia.phone_number || "—",
      },
      {
        label: "Envio",
        value: instancia.envio_pausado ? "Pausado" : "Ativo",
        tone: instancia.envio_pausado ? "warning" : "success",
      },
    ];
  }, [instancia]);

  if (error) {
    return (
      <PageContainer>
        <EntityHeader
          title="Instancia"
          secondaryActions={[
            {
              label: "Voltar",
              icon: <FiArrowLeft />,
              onClick: () => navigate("/whatsapp/instancias"),
            },
          ]}
        />
        <ErrorMsg>{error}</ErrorMsg>
      </PageContainer>
    );
  }

  if (!instancia) {
    return (
      <PageContainer>
        <EntityHeader title="Carregando..." />
        <EmptyMsg>Carregando dados...</EmptyMsg>
      </PageContainer>
    );
  }

  const podeConectar =
    podeGerenciar &&
    (instancia.status === "DESCONECTADA" || instancia.status === "EXPIRADA");

  return (
    <PageContainer>
      <EntityHeader
        title={instancia.nome_interno}
        subtitle="Instância WhatsApp Web"
        primaryAction={
          podeConectar
            ? {
                label: "Conectar (gerar QR)",
                onClick: handleIniciar,
              }
            : null
        }
        secondaryActions={[
          {
            label: "Voltar",
            icon: <FiArrowLeft />,
            onClick: () => navigate("/whatsapp/instancias"),
          },
          {
            label: "Atualizar",
            icon: <FiRefreshCw />,
            onClick: carregar,
          },
        ]}
      />

      <KpiStrip items={kpis} />

      <Card>
        <SectionTitle>Status atual</SectionTitle>
        <DetailGrid>
          <FieldRow>
            <FieldLabel>Status</FieldLabel>
            <FieldValue>
              <Pill $tone={STATUS_INSTANCIA_TONE[instancia.status] || "neutral"}>
                {STATUS_INSTANCIA_LABEL[instancia.status] || instancia.status}
              </Pill>
            </FieldValue>
          </FieldRow>
          <FieldRow>
            <FieldLabel>Número conectado</FieldLabel>
            <FieldValue>{instancia.phone_number || "—"}</FieldValue>
          </FieldRow>
          <FieldRow>
            <FieldLabel>Último evento</FieldLabel>
            <FieldValue>{fmtData(instancia.ultimo_evento_em) || "—"}</FieldValue>
          </FieldRow>
          <FieldRow>
            <FieldLabel>Criada por</FieldLabel>
            <FieldValue>{instancia.criada_por_nome || "—"}</FieldValue>
          </FieldRow>
          <FieldRow>
            <FieldLabel>Criada em</FieldLabel>
            <FieldValue>{fmtData(instancia.created_at) || "—"}</FieldValue>
          </FieldRow>
        </DetailGrid>

        {instancia.status === "AGUARDANDO_QR" && instancia.qr_code_atual && (
          <div style={{ marginTop: 16 }}>
            <SecondaryButton type="button" onClick={() => setQrOpen(true)}>
              Ver QR Code
            </SecondaryButton>
            <InlineHint style={{ marginLeft: 12 }}>
              Escaneie no WhatsApp do celular para conectar.
            </InlineHint>
          </div>
        )}
      </Card>

      {podeGerenciar && (
        <Card>
          <SectionTitle>Kill switch (envio)</SectionTitle>
          <KillSwitchBox $pausado={instancia.envio_pausado}>
            <div style={{ flex: 1 }}>
              <strong>
                {instancia.envio_pausado
                  ? "Envio PAUSADO"
                  : "Envio ATIVO"}
              </strong>
              <div style={{ fontSize: 13, marginTop: 4 }}>
                {instancia.envio_pausado
                  ? `Motivo: ${instancia.motivo_pausa || "—"}`
                  : "Mensagens podem ser enviadas normalmente."}
              </div>
              <InlineHint>
                Pausar bloqueia envio sem fechar a sessao — inbound continua chegando.
              </InlineHint>
            </div>
            <div>
              {instancia.envio_pausado ? (
                <AsyncButton
                  variant="primary"
                  onClick={handleRetomar}
                  successMessage="Envio retomado"
                >
                  <FiPlay style={{ marginRight: 4 }} /> Retomar
                </AsyncButton>
              ) : (
                <SecondaryButton
                  type="button"
                  onClick={() => setPauseOpen(true)}
                >
                  <FiPause style={{ marginRight: 4 }} /> Pausar
                </SecondaryButton>
              )}
            </div>
          </KillSwitchBox>
        </Card>
      )}

      {podeGerenciar && instancia.status === "CONECTADA" && (
        <Card>
          <Block>
            <SectionTitle>Sessão</SectionTitle>
            <p style={{ fontSize: 13, marginBottom: 8 }}>
              Forçar logout no serviço Node. Para reconectar, será preciso
              escanear o QR novamente.
            </p>
            <AsyncButton
              variant="danger"
              onClick={handleDesconectar}
              successMessage="Sessão desconectada"
            >
              <FiPower style={{ marginRight: 4 }} /> Desconectar
            </AsyncButton>
          </Block>
        </Card>
      )}

      <SlideOverPanel
        open={qrOpen}
        onClose={() => setQrOpen(false)}
        title="QR Code WhatsApp"
        subtitle="Abra o WhatsApp do celular > Dispositivos conectados > Conectar dispositivo"
      >
        {instancia.qr_code_atual ? (
          <QrFrame>
            <img
              src={
                instancia.qr_code_atual.startsWith("data:")
                  ? instancia.qr_code_atual
                  : `data:image/png;base64,${instancia.qr_code_atual}`
              }
              alt="QR Code"
            />
            <div className="countdown">
              QR expira em ~60s. Polling automatico — feche quando conectar.
            </div>
            {instancia.qr_expira_em && (
              <div className="countdown">
                Expira: {fmtData(instancia.qr_expira_em)}
              </div>
            )}
          </QrFrame>
        ) : (
          <EmptyMsg>
            QR ainda não foi gerado. Clique em "Conectar" no detalhe e
            aguarde alguns segundos.
          </EmptyMsg>
        )}
      </SlideOverPanel>

      <SlideOverPanel
        open={pauseOpen}
        onClose={() => {
          setPauseOpen(false);
          setPauseMotivo("");
        }}
        title="Pausar envio"
        subtitle="Bloqueia novas mensagens. Inbound continua chegando."
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            handlePausar();
          }}
        >
          <Field>
            <FieldLabel>Motivo</FieldLabel>
            <Textarea
              value={pauseMotivo}
              onChange={(e) => setPauseMotivo(e.target.value)}
              placeholder="Ex.: Aguardando aprovação do gestor para alterar fluxo"
              rows={3}
              autoFocus
              minLength={5}
              required
            />
            <InlineHint>
              Será auditado. Mínimo 5 caracteres.
            </InlineHint>
          </Field>

          <FormFooter>
            <SecondaryButton
              type="button"
              onClick={() => setPauseOpen(false)}
            >
              Cancelar
            </SecondaryButton>
            <AsyncButton
              variant="danger"
              onClick={handlePausar}
              successMessage="Envio pausado"
              silentError
            >
              Pausar envio
            </AsyncButton>
          </FormFooter>
        </Form>
      </SlideOverPanel>
    </PageContainer>
  );
}
